/**
 * SectionTabsScroller
 *
 * Wraps the `header-section-tabs-slot` container — the horizontal strip
 * the section tabs portal into — and adds left/right chevron buttons
 * that appear when the strip overflows. Click a chevron to scroll by
 * roughly one tab-width.
 *
 * Why a scroller and not just `overflow-x-auto + scrollbar-none`: with
 * the scrollbar hidden, mouse-only users have no affordance to discover
 * tabs that don't fit (kbd users can arrow-key through). Chevrons make
 * overflow visible and clickable without forcing a scrollbar back in.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SCROLL_STEP = 140; // ~ one tab-width with the new 120px min-width

export default function SectionTabsScroller() {
  const slotRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [overflowLeft, setOverflowLeft] = useState(false);
  const [overflowRight, setOverflowRight] = useState(false);
  const [hasToolbar, setHasToolbar] = useState(false);

  const recompute = useCallback(() => {
    const el = slotRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setOverflowLeft(scrollLeft > 1);
    setOverflowRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = slotRef.current;
    if (!el) return;
    recompute();
    el.addEventListener("scroll", recompute, { passive: true });
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    // Children are portaled in async — observe mutations so we
    // recompute when tabs land in the DOM.
    const mo = new MutationObserver(recompute);
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      el.removeEventListener("scroll", recompute);
      ro.disconnect();
      mo.disconnect();
    };
  }, [recompute]);

  // Track whether the toolbar slot is empty so we only show the
  // separator when something's portaled into it.
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const update = () => setHasToolbar(el.childElementCount > 0);
    update();
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true });
    return () => mo.disconnect();
  }, []);

  const scrollBy = (delta: number) => {
    slotRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  };

  return (
    <div className="h-10 flex items-end bg-slate-50 border-b border-slate-100">
      {/* Left chevron — rendered only when there's content scrolled out
          of view to the left. The column doesn't reserve space when no
          overflow exists, so projects with only a couple of sections
          don't have empty 40px rails on either side of the strip. */}
      {overflowLeft && (
        <div className="w-10 h-10 shrink-0 flex items-center justify-center">
          <button
            type="button"
            onClick={() => scrollBy(-SCROLL_STEP)}
            aria-label="Scroll sections left"
            className="size-7 flex items-center justify-center text-slate-500 bg-slate-100 hover:text-indigo-600 hover:bg-slate-200 rounded-md transition-colors"
          >
            <ChevronLeft className="size-4" strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* Scrollable tabs strip — section tabs + the Add Section button
          portal into this. */}
      <div
        id="header-section-tabs-slot"
        ref={slotRef}
        className="flex-1 min-w-0 h-10 flex items-end gap-0 overflow-x-auto scrollbar-none scroll-smooth"
      />

      {/* Right chevron — mirror of left. */}
      {overflowRight && (
        <div className="w-10 h-10 shrink-0 flex items-center justify-center">
          <button
            type="button"
            onClick={() => scrollBy(SCROLL_STEP)}
            aria-label="Scroll sections right"
            className="size-7 flex items-center justify-center text-slate-500 bg-slate-100 hover:text-indigo-600 hover:bg-slate-200 rounded-md transition-colors"
          >
            <ChevronRight className="size-4" strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* Vertical divider — only when the toolbar has something in it,
          so an empty toolbar doesn't leave a stray line floating. */}
      {hasToolbar && <div className="self-center mx-1 w-px h-5 bg-slate-200" />}

      {/* Toolbar slot — Timeline button portals here so it never scrolls
          off-screen with the tabs. */}
      <div
        id="header-section-tabs-toolbar-slot"
        ref={toolbarRef}
        className="shrink-0 h-10 flex items-center pr-3 gap-2"
      />
    </div>
  );
}
